import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { useI18n } from "../i18n";

const Counter = ({ end, suffix = "", duration = 1800, testId }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.1 });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) {
      // Fallback: after 3s, show final value even if useInView didn't fire (mobile edge case)
      const t = setTimeout(() => setVal(end), 3000);
      return () => clearTimeout(t);
    }
    const startTime = performance.now();
    const tick = (now) => {
      const p = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(end * eased));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, end, duration]);

  return <span ref={ref} data-testid={testId}>{val.toLocaleString("tr-TR")}{suffix}</span>;
};

const About = ({ stats: liveStats }) => {
  const { t } = useI18n();

  const stats = [
    { value: liveStats?.completed ?? 17, suffix: "+", label: t.about.projects, testId: "stat-completed" },
    { value: liveStats?.ongoing ?? 5, suffix: "+", label: t.about.ongoing, testId: "stat-ongoing" },
    { value: liveStats?.cities ?? 5, suffix: "", label: t.about.cities, testId: "stat-cities" },
    { value: 4, suffix: "", label: t.about.offices, testId: "stat-offices" },
    { value: 200000, suffix: " m²+", label: t.about.area, testId: "stat-area" },
  ];

  return (
    <section id="about" className="relative py-24 md:py-32 bg-[var(--bg)] overflow-hidden" data-testid="about-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5 lg:sticky lg:top-32 self-start"
        >
          <div className="overline mb-4">{t.about.overline}</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-white leading-[1.05]">
            {t.about.title}
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="lg:col-span-7 space-y-8"
        >
          <p className="text-lg leading-relaxed text-[var(--text-main)]">{t.about.body}</p>
          <p className="text-base leading-relaxed text-[var(--text-muted)]">{t.about.body2}</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4">
            <div className="border-l-2 border-[var(--primary)] pl-6">
              <div className="overline mb-2">{t.about.visionTitle}</div>
              <p className="text-sm text-[var(--text-main)] leading-relaxed">{t.about.vision}</p>
            </div>
            <div className="border-l-2 border-[var(--silver)] pl-6">
              <div className="overline mb-2">{t.about.missionTitle}</div>
              <p className="text-sm text-[var(--text-main)] leading-relaxed">{t.about.mission}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-[var(--border)] mt-12">
            {stats.map((s) => (
              <div key={s.label} className="bg-[var(--bg)] p-6 md:p-8" data-testid={`stat-card-${s.testId}`}>
                <div className="font-display text-4xl md:text-5xl font-light text-white tracking-tight mb-2">
                  <Counter end={s.value} suffix={s.suffix} testId={s.testId} />
                </div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--text-muted)]">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
